import type { Ref } from "vue";
import { useDroppable } from "@vue-dnd-kit/core";
import { Facade } from "@/drawing";
import { Logger } from "@/logger";
import DraggerMenuState from "@/stores/DraggerMenuState";
import NoteMenuState from "@/stores/NoteMenuState";
import type { Layer } from "@/types";
import ScaleMenuState from "@/stores/ScaleMenuState";
import type { CanvasDragData } from "@/types/dnd.types";

export function useCanvasDrop(canvas: Ref<HTMLCanvasElement | undefined>) {
  const { elementRef: dropRef } = useDroppable({
    groups: ["canvas-entity"],
    events: {
      onDrop: async (_store, payload) => {
        const item = payload.items?.[0]?.data as CanvasDragData | undefined;
        if (!item) return false;

        const pos = _store.pointerPosition.current.value;
        if (!pos || !canvas.value) return false;

        const rect = canvas.value.getBoundingClientRect();
        const screenPos = { x: pos.x - rect.left, y: pos.y - rect.top };
        if (!Facade.isInitialized) return false;

        // End any in-progress placement (e.g. CopyDragger waiting for its
        // next point) before creating an entity through this separate
        // drag-and-drop pipeline, which never goes through DraggerManager
        // and so never gives the active tool a normal deactivate(). No-op
        // by default for tools that don't need it.
        Facade.tools.current?.cancelPendingPlacement();

        const world = Facade.view.screenToWorld(screenPos);

        if (item.type === "photo" && world) {
          const photoLayer: Layer = DraggerMenuState.getPhotoLayer() ?? {
            id: "0",
            name: "0",
          };
          try {
            Facade.layers.ensureLayerExists(photoLayer, true);

            // find the active scale
            const scaleConfig = ScaleMenuState.computeActiveScale();
            const scale =
              scaleConfig?.props.textsize ?? scaleConfig?.props.textstyle?.size;
            const photoNoteConfig = NoteMenuState.getPhotoNoteConfig();
            Facade.photoMarkers.createAtPosition(
              world.x,
              world.y,
              photoLayer.name,
              scaleConfig?.props.textstyle,
              scale,
              true,
              photoNoteConfig.props
            );
            return true;
          } catch(e) {
            Logger.error(`useCanvasDrop : there was a problem inserting photo`);
            Logger.captureException(e, {
              itemType: item.type,
              worldPosition: world,
              screenPosition: screenPos,
              layerName: photoLayer.name,
            });
            return true;
          }
        }

        if (item.type === "note") {
          const layer: Layer = DraggerMenuState.getLayer(
            item.config.props.layername ?? ""
          ) ?? { id: "0", name: "0" };
          try {
            Facade.layers.ensureLayerExists(layer);
            Facade.text.createAtPosition(
              world.x,
              world.y,
              item.label,
              item.config
            );
            return true;
          } catch(e) {
            Logger.error(`useCanvasDrop : there was a problem inserting note`);
            Logger.captureException(e, {
              itemType: item.type,
              itemLabel: item.label,
              worldPosition: world,
              screenPosition: screenPos,
              layerName: layer.name,
            });
            return true;
          }
        }

        return false;
      },
    },
  });

  return { dropRef };
}
